<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-sitemap</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Centros de costo</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Imputación de CxC y CxP · análisis por centro
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 mr-2" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
      <v-btn color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" @click="openCreate">
        <v-icon left small>fas fa-plus</v-icon> Nuevo centro
      </v-btn>
    </v-row>

    <v-card outlined class="modern-card mx-4 mb-6">
      <v-data-table :headers="headers" :items="centers" :loading="loading" hide-default-footer class="elevation-0" no-data-text="Cree un centro de costo.">
        <template v-slot:item.is_active="{ item }">
          <v-chip x-small :color="item.is_active ? 'success' : 'grey'" class="white--text">
            {{ item.is_active ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openEdit(item)"><v-icon small>fas fa-pen</v-icon></v-btn>
          <v-btn v-if="item.is_active" icon small @click="deactivate(item)"><v-icon small>fas fa-ban</v-icon></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card outlined class="modern-card mx-4">
      <div class="pa-4 subtitle-2 font-weight-bold">Análisis CxC / CxP</div>
      <v-data-table :headers="analysisHeaders" :items="analysis" :loading="loading" hide-default-footer class="elevation-0" no-data-text="Sin documentos imputados.">
        <template v-slot:item.ar_total="{ item }">{{ item.ar_total | moneda }}</template>
        <template v-slot:item.ar_balance="{ item }">{{ item.ar_balance | moneda }}</template>
        <template v-slot:item.ap_total="{ item }">{{ item.ap_total | moneda }}</template>
        <template v-slot:item.ap_balance="{ item }">{{ item.ap_balance | moneda }}</template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="480">
      <v-card>
        <v-card-title class="font-weight-black">{{ editingId ? 'Editar centro' : 'Nuevo centro' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.code" label="Código" outlined dense :disabled="!!editingId" />
          <v-text-field v-model="form.name" label="Nombre" outlined dense />
          <v-textarea v-model="form.description" label="Descripción" outlined dense rows="2" />
          <v-select v-model="form.parent_id" :items="parentItems" item-text="text" item-value="value" label="Padre (opcional)" outlined dense clearable />
          <v-text-field v-model="form.sort_order" type="number" label="Orden" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {
  createCostCenter,
  deactivateCostCenter,
  fetchCostCenterAnalysis,
  fetchCostCenters,
  updateCostCenter,
} from '@/api/finance'

export default {
  name: 'FinanceCostCenters',
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      editingId: null,
      centers: [],
      analysis: [],
      form: { code: '', name: '', description: '', parent_id: null, sort_order: 0 },
      headers: [
        { text: 'Código', value: 'code' },
        { text: 'Nombre', value: 'name' },
        { text: 'Padre', value: 'parent_code' },
        { text: 'Hijos', value: 'children_count' },
        { text: 'Estado', value: 'is_active' },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 88 },
      ],
      analysisHeaders: [
        { text: 'Centro', value: 'code' },
        { text: 'Nombre', value: 'name' },
        { text: 'CxC docs', value: 'ar_count' },
        { text: 'CxC total', value: 'ar_total', align: 'end' },
        { text: 'CxC saldo', value: 'ar_balance', align: 'end' },
        { text: 'CxP docs', value: 'ap_count' },
        { text: 'CxP total', value: 'ap_total', align: 'end' },
        { text: 'CxP saldo', value: 'ap_balance', align: 'end' },
      ],
    }
  },
  computed: {
    parentItems() {
      return this.centers
        .filter(c => !this.editingId || c.id !== this.editingId)
        .map(c => ({ value: c.id, text: `${c.code} · ${c.name}` }))
    },
  },
  methods: {
    async fetchAll() {
      this.loading = true
      try {
        const [centers, analysis] = await Promise.all([
          fetchCostCenters({ active_only: false }),
          fetchCostCenterAnalysis(),
        ])
        this.centers = centers.items || []
        this.analysis = analysis.items || []
      } catch (e) {
        notifications.showError('No se pudieron cargar los centros de costo')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingId = null
      this.form = { code: '', name: '', description: '', parent_id: null, sort_order: 0 }
      this.dialog = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.form = {
        code: item.code,
        name: item.name,
        description: item.description || '',
        parent_id: item.parent_id,
        sort_order: item.sort_order || 0,
      }
      this.dialog = true
    },
    async submit() {
      this.saving = true
      try {
        if (this.editingId) {
          await updateCostCenter(this.editingId, this.form)
          notifications.showSuccess('Centro actualizado')
        } else {
          await createCostCenter(this.form)
          notifications.showSuccess('Centro creado')
        }
        this.dialog = false
        await this.fetchAll()
      } catch (e) {
        notifications.showError((e.response && e.response.data && e.response.data.error) || 'No se pudo guardar')
      } finally {
        this.saving = false
      }
    },
    async deactivate(item) {
      if (!confirm(`¿Desactivar el centro ${item.code}?`)) return
      try {
        await deactivateCostCenter(item.id)
        notifications.showSuccess('Centro desactivado')
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo desactivar')
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
